import React from 'react';
import { ContractText, ContractBadge } from '@/components/system/ContractUI';

/**
 * DynamicSectionRenderer - Contract-Driven UI Renderer
 * Authority: Law 66.1 (Dynamic UI Labels)
 * 
 * Renders task detail sections based on contract configuration.
 * NO hard-coded labels allowed - all must come from ui_render_config.
 */

export function DynamicSectionRenderer({ sections, item }) {
    // CRITICAL: Check for undefined/null BEFORE accessing .length
    if (!sections || !Array.isArray(sections) || sections.length === 0) {
        return <ContractText variant="caption" className="italic text-muted-foreground">No content configuration available</ContractText>;
    }

    return (
        <div className="space-y-6">
            {(sections || []).map((section, idx) => {
                const value = extractValue(item, section.field);

                // Skip if value missing and not required
                if (!value && !section.required) return null;

                // Emit warning if required field missing (Law 66.1)
                if (!value && section.required) {
                    console.warn(`[UI_CONTRACT_MISSING_FIELD] Required field '${section.field}' not found in item`, item);
                    return (
                        <div key={idx} className="p-3 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                            ⚠️ Required field <code>{section.field}</code> missing from agent output
                        </div>
                    );
                }

                return (
                    <Section key={idx} label={section.label}>
                        {renderByType(value, section.render_type, item)}
                    </Section>
                );
            })}
        </div>
    );
}

// Section wrapper - only renders label if provided (contract-driven)
function Section({ label, children }) {
    return (
        <div className="space-y-2">
            {label && (
                <ContractText variant="sectionLabel">
                    {label}
                </ContractText>
            )}
            <div className="text-brand-700">{children}</div>
        </div>
    );
}

// Extract value from item (supports nested paths)
function extractValue(item, field) {
    // Try payload first, then top-level
    return item.payload?.[field] || item[field];
}

// Render value based on type specification from contract
function renderByType(value, renderType, item) {
    switch (renderType) {
        case 'email_preview':
            return (
                <div className="border border-brand-200 rounded-lg overflow-hidden">
                    <div className="bg-brand-50 px-4 py-3 border-b border-brand-200 flex justify-between items-center">
                        <div>
                            <ContractText variant="caption" className="font-medium text-brand-600">Subject:</ContractText>
                            <ContractText variant="body" className="font-semibold text-brand-900 mt-1">
                                {value.subject || 'No subject'}
                            </ContractText>
                        </div>
                        <span className="bg-white px-2 py-1 rounded border border-brand-200 text-xs text-brand-500">
                            HTML
                        </span>
                    </div>
                    <div
                        className="p-4 bg-white prose prose-sm max-w-none text-slate-800"
                        dangerouslySetInnerHTML={{ __html: value.html_body || '<p>No content</p>' }}
                    />
                </div>
            );

        case 'recipient_count':
            const count = typeof value === 'number' ? value : 0;
            const quotaRemaining = 15; // TODO: Get from backend quota service
            return (
                <div className="flex items-center gap-4">
                    <div className="flex items-baseline gap-2">
                        <ContractText variant="stat" className="text-3xl">{count}</ContractText>
                        <ContractText variant="body">
                            recipient{count !== 1 ? 's' : ''} selected
                        </ContractText>
                    </div>
                    <div className="ml-auto text-xs text-muted-foreground">
                        Daily quota: <span className="font-semibold text-brand-700">{quotaRemaining}</span> emails remaining
                    </div>
                </div>
            );

        case 'text':
            return <ContractText variant="body" className="leading-relaxed whitespace-pre-wrap">{String(value)}</ContractText>;

        case 'callout':
            return (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                    <ContractText variant="body" className="italic text-slate-700 whitespace-pre-wrap">{String(value)}</ContractText>
                </div>
            );

        case 'tag_list': {
            // Array of evidence/data points (e.g. the ceo_approval "basis").
            // Render each as its own readable line so numbers stay legible.
            const items = Array.isArray(value) ? value : [value];
            return (
                <ul className="space-y-2 list-disc pl-5">
                    {items.map((entry, i) => (
                        <li key={i}>
                            <ContractText variant="body" className="leading-relaxed whitespace-pre-wrap">
                                {String(entry)}
                            </ContractText>
                        </li>
                    ))}
                </ul>
            );
        }

        case 'badge':
            return (
                <ContractBadge intent="info">
                    {String(value)}
                </ContractBadge>
            );

        case 'verdict':
            const verdictColor = value === 'ACCEPT' || value === 'approved'
                ? 'text-emerald-600 bg-emerald-50 border-emerald-200'
                : value === 'DISCARD' || value === 'rejected'
                    ? 'text-red-600 bg-red-50 border-red-200'
                    : 'text-slate-600 bg-slate-50 border-slate-200';
            return (
                <div className={`inline-flex px-3 py-1.5 rounded-lg border font-semibold text-sm ${verdictColor}`}>
                    {String(value)}
                </div>
            );

        case 'link':
            // Clickable external link, opens in a new tab. Used by the LinkedIn
            // comment card's post URL (WI-LINKEDIN-COMMENT-BUILD-MINIMAL-001).
            return (
                <a
                    href={String(value)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-600 underline break-all hover:text-brand-800 text-base"
                >
                    {String(value)}
                </a>
            );

        case 'grounding_badge':
            // Compact provenance indicator for the LinkedIn comment card.
            // WI-LINKEDIN-COMMENT-KB-GATE-REQUESTY-001.
            // Business language only — tells the CEO how many knowledge sources
            // back the drafted comment without exposing internal field names or
            // technical terms.  Safe: renders nothing if value is empty/falsy.
            if (!value) return null;
            return (
                <div
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50"
                    style={{ minHeight: '36px' }}
                    aria-label={String(value)}
                >
                    <span
                        className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0"
                        aria-hidden="true"
                    />
                    <span
                        className="text-emerald-800 font-semibold"
                        style={{ fontSize: '16px', lineHeight: '1.4' }}
                    >
                        {String(value)}
                    </span>
                </div>
            );

        default:
            // Fallback: render as string
            return <ContractText variant="body">{String(value)}</ContractText>;
    }
}
